import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Typography,
  Switch,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from 'axios';

interface ExamType {
  id: string;
  name: string;
  code: string;
  weight?: number;
  description?: string;
  isActive: boolean;
}

const ExamTypeMaster: React.FC = () => {
  const [items, setItems] = useState<ExamType[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ExamType | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    weight: '',
    description: '',
    isActive: true,
  });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/system-settings/masters/examType');
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch exam types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      code: '',
      weight: '',
      description: '',
      isActive: true,
    });
    setDialogOpen(true);
  };

  const handleEdit = (item: ExamType) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      code: item.code,
      weight: item.weight?.toString() || '',
      description: item.description || '',
      isActive: item.isActive,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        name: formData.name,
        code: formData.code,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        description: formData.description,
        isActive: formData.isActive,
      };

      if (editingItem) {
        await axios.patch(`/system-settings/masters/examType/${editingItem.id}`, payload);
      } else {
        await axios.post('/system-settings/masters/examType', payload);
      }
      setDialogOpen(false);
      fetchItems();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save exam type');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this exam type?')) {
      try {
        await axios.delete(`/system-settings/masters/examType/${id}`);
        fetchItems();
      } catch (error: any) {
        alert(error.response?.data?.message || 'Failed to delete exam type');
      }
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await axios.patch(`/system-settings/masters/examType/${id}/toggle-status`);
      setItems((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, isActive: !currentStatus } : item
        )
      );
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to toggle status');
      fetchItems();
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Exam Types</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          New Exam Type
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Weight (%)</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                    No records found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.code}</TableCell>
                  <TableCell>{item.weight || '-'}</TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell>
                    <Switch
                      checked={item.isActive}
                      onChange={() => handleToggleStatus(item.id, item.isActive)}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEdit(item)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(item.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Exam Type' : 'Create Exam Type'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              fullWidth
              required
              helperText="Unique code (e.g., MID_TERM, FINAL)"
            />
            <TextField
              label="Weight (%)"
              type="number"
              value={formData.weight}
              onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
              fullWidth
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              helperText="Optional weightage for final calculation"
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExamTypeMaster;
